import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import {
  useScoringRules,
  type LeagueName,
  type WCRulebook,
  type WCRulebookSection,
} from './api';
import { RulebookDiff } from './RulebookDiff';

export function RulesTab({ league }: { league: LeagueName }) {
  const { data, isLoading, error } = useScoringRules(league);

  if (isLoading) {
    return (
      <div className="rounded-md border bg-card px-4 py-12 text-center text-sm text-muted-foreground">
        Loading...
      </div>
    );
  }
  if (error) {
    const msg = error instanceof Error ? error.message : 'Failed to load scoring rules';
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-6 text-sm text-destructive">
        {msg}
      </div>
    );
  }

  const scoring = data?.data;
  if (!scoring) {
    return (
      <div className="rounded-md border border-dashed bg-muted/20 px-4 py-12 text-center text-sm text-muted-foreground">
        No rules surfaced.
      </div>
    );
  }

  // WC has a structured rulebook served from scoring-engine; render that as
  // the primary surface. EPL still uses the simpler engine-constant cards.
  if (league === 'WC' && data?.rulebook) {
    return <WCRulebookView rulebook={data.rulebook} rulebookV1={data.rulebook_v1 ?? null} />;
  }

  const squadRules: Array<[string, string]> = [
    ['Squad size', '15 players'],
    ['Starters', '11 players'],
    ['Bench', '4 players'],
    ['Budget cap', '200 (2x FPL prices)'],
    ['Goalkeepers required', '2'],
    ['Max players per real-world team', '3'],
    ['Captain', '1 player, 2x points'],
    ['Auto-sub', 'Bench priority replaces non-playing starters'],
  ];
  const minutes = scoring.minutes ?? scoring.appearance ?? { played_any: 0, played_60_plus: 0 };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Scoring version</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="font-mono text-sm">{scoring.version}</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Squad composition</CardTitle>
        </CardHeader>
        <CardContent>
          <RulesTable rows={squadRules} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Minutes</CardTitle>
        </CardHeader>
        <CardContent>
          <RulesTable
            rows={[
              ['Played any minutes', minutes.played_any],
              ['Played 60+ minutes additional', minutes.played_60_plus],
            ]}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Goals</CardTitle>
        </CardHeader>
        <CardContent>
          <RulesTable rows={Object.entries(scoring.goal)} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Clean sheets</CardTitle>
        </CardHeader>
        <CardContent>
          <RulesTable rows={Object.entries(scoring.clean_sheet)} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Other scoring</CardTitle>
        </CardHeader>
        <CardContent>
          <RulesTable
            rows={[
              ['Assist', scoring.assist],
              ['Yellow card', scoring.yellow_card],
              ['Red card', scoring.red_card],
              ['Saves per point', scoring.saves_per_point],
              ['Saves point value', scoring.saves_point_value],
              ['Bonus multiplier', scoring.bonus_multiplier ?? 0],
              ['Own goal', scoring.own_goal],
              ['Penalty missed', scoring.penalty_missed],
              ['Penalty saved', scoring.penalty_saved],
              ...(scoring.free_kick_bonus != null
                ? ([['Free-kick goal bonus', scoring.free_kick_bonus]] as Array<[string, number]>)
                : []),
              ...(scoring.transfer_hit != null
                ? ([['Transfer hit', scoring.transfer_hit]] as Array<[string, number]>)
                : []),
            ]}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function WCRulebookView({
  rulebook,
  rulebookV1,
}: {
  rulebook: WCRulebook;
  rulebookV1: WCRulebook | null;
}) {
  const [showDiff, setShowDiff] = useState(false);
  const canDiff = rulebookV1 != null;

  const versionCard = (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Scoring version</CardTitle>
        <Button
          type="button"
          size="sm"
          variant={showDiff ? 'default' : 'outline'}
          disabled={!canDiff}
          onClick={() => setShowDiff((prev) => !prev)}
          title={canDiff ? undefined : 'No v1 rulebook returned by the API'}
        >
          {showDiff ? 'Hide diff vs v1' : 'Diff vs v1 rulebook'}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="font-mono text-sm">
          {rulebook.version}
          <span className="ml-2 text-muted-foreground">— {rulebook.title}</span>
        </div>
      </CardContent>
    </Card>
  );

  if (showDiff && rulebookV1) {
    return (
      <div className="space-y-4">
        {versionCard}
        <RulebookDiff v1={rulebookV1} v2={rulebook} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {versionCard}
      <div className="grid gap-4 lg:grid-cols-2">
        {rulebook.sections.map((section) => (
          <RulebookSectionCard key={section.id} section={section} />
        ))}
      </div>
    </div>
  );
}

function RulebookSectionCard({ section }: { section: WCRulebookSection }) {
  const span = section.span === 'full' ? 'lg:col-span-2' : '';
  return (
    <Card className={span}>
      <CardHeader>
        <CardTitle className="text-base">{section.heading}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {section.kind === 'rounds' ? <RoundsList rows={section.rows} /> : null}
        {section.kind === 'rows' ? (
          <RulesTable rows={section.rows.map((r) => [r.label, r.value])} />
        ) : null}
        {section.kind === 'bullets' ? <BulletsList items={section.bullets} /> : null}
        {section.kind === 'superpowers' ? <SuperpowersGrid cards={section.cards} /> : null}
        {section.footer ? (
          <p className="text-xs italic text-muted-foreground">{section.footer}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function RoundsList({ rows }: { rows: Array<{ code: string; name: string; dates: string; teams: number }> }) {
  return (
    <table className="w-full text-sm">
      <tbody>
        {rows.map((row) => (
          <tr key={row.code} className="border-t first:border-t-0">
            <td className="py-1.5 pr-3 font-mono text-xs text-muted-foreground">{row.code}</td>
            <td className="py-1.5 pr-4 font-medium">{row.name}</td>
            <td className="py-1.5 pr-4 text-muted-foreground">{row.dates}</td>
            <td className="py-1.5 text-right text-muted-foreground">{row.teams} teams</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BulletsList({ items }: { items: string[] }) {
  return (
    <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

function SuperpowersGrid({
  cards,
}: {
  cards: Array<{ name: string; quota: string; description: string }>;
}) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {cards.map((c) => (
        <div key={c.name} className="rounded-md border bg-muted/20 px-3 py-2">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-sm font-semibold">{c.name}</div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {c.quota}
            </div>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{c.description}</p>
        </div>
      ))}
    </div>
  );
}

function RulesTable({ rows }: { rows: Array<[string, string | number]> }) {
  return (
    <table className="w-full text-sm">
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label} className="border-t first:border-t-0">
            <td className="py-1.5 pr-4 text-muted-foreground">{label}</td>
            <td className="py-1.5 text-right font-mono font-medium">{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
